import { useMemo } from 'react';
import { isElementaryCloudTest } from '@elementary/common-ui/utils/common';
import { TEST_TEMPLATES } from '@elementary/common-ui/constants/queryKeys';
import { WarehouseType } from '@elementary/common-ui/types';
import { useQuery } from '@tanstack/react-query';
import useHasDbtProjects from '@elementary/common-ui/hooks/useHasDbtProjects';
import useSelectedEnv from '@elementary/common-ui/hooks/useSelectedEnv';
import { useCodeRepositoryIntegration } from 'shared/hooks/integrations';
import { AddTestForm, ColumnResource, Resource } from 'shared/types';
import * as testManagementApi from 'testManagement/services/api';
import { getWizardSelectableTests } from 'testManagement/utils/utils';
import { elementaryColumnTests, elementaryTableTests } from './elementaryTests';
import { getCloudTestDisabledReason } from './utils';

type Props = {
  resourceType: AddTestForm['resourceType'];
  selectedResourceIds: string[];
  tableResourcesData: Resource[];
  columnsResourcesData: ColumnResource[];
  warehouseType?: WarehouseType;
  enabled?: boolean;
};

const useWizardTestCatalog = ({
  resourceType,
  selectedResourceIds,
  tableResourcesData,
  columnsResourcesData,
  warehouseType,
  enabled = true,
}: Props) => {
  const selectedEnvId = useSelectedEnv();
  const hasDbtProjects = useHasDbtProjects();
  const { shouldConnectCodeRepo } = useCodeRepositoryIntegration({
    envId: selectedEnvId,
  });

  const { data: testTemplates, isLoading } = useQuery(
    [TEST_TEMPLATES, resourceType],
    () =>
      testManagementApi.getTestTemplates(
        resourceType === 'tables' ? 'table' : 'column',
        selectedEnvId!,
      ),
    { enabled: enabled && !!selectedEnvId },
  );

  const selectedTableResourcesData = useMemo(() => {
    if (!selectedResourceIds.length || resourceType !== 'tables') return [];

    return tableResourcesData.filter((resource) =>
      selectedResourceIds.includes(resource.id),
    );
  }, [tableResourcesData, resourceType, selectedResourceIds]);

  const selectedColumnResourcesData = useMemo(() => {
    if (!selectedResourceIds.length || resourceType !== 'columns') return [];

    return columnsResourcesData.filter((resource) =>
      selectedResourceIds.includes(resource.id),
    );
  }, [columnsResourcesData, resourceType, selectedResourceIds]);

  const canUseDbtTests =
    !shouldConnectCodeRepo &&
    hasDbtProjects &&
    ((resourceType === 'tables' &&
      selectedTableResourcesData.every(
        (table) => table.source_type === 'dbt',
      )) ||
      (resourceType === 'columns' &&
        selectedColumnResourcesData.every(
          (column) => column.source_type === 'dbt',
        )));

  const elementaryTests = useMemo(() => {
    const tests =
      resourceType === 'tables' ? elementaryTableTests : elementaryColumnTests;

    if (canUseDbtTests) {
      return tests;
    }

    return tests.filter((test) => isElementaryCloudTest(test.test_name));
  }, [resourceType, canUseDbtTests]);

  const testTemplatesData = useMemo(() => {
    if (canUseDbtTests) {
      return testTemplates?.test_templates;
    }
    return testTemplates?.test_templates.filter((test) =>
      isElementaryCloudTest(test.test_name),
    );
  }, [canUseDbtTests, testTemplates]);

  const selectableTests = useMemo(
    () =>
      getWizardSelectableTests({
        apiTemplates: testTemplatesData,
        elementaryTests,
      }).map((test) => {
        const disabledReason = getCloudTestDisabledReason({
          packageName: test.package,
          testType: test.testType,
          warehouseType,
          selectedTableResourcesData,
          selectedTargetsCount: selectedResourceIds.length,
        });
        return disabledReason ? { ...test, disabledReason } : test;
      }),
    [
      testTemplatesData,
      elementaryTests,
      warehouseType,
      selectedTableResourcesData,
      selectedResourceIds.length,
    ],
  );

  return {
    isLoading: enabled && isLoading,
    elementaryTests,
    testTemplatesData,
    selectableTests,
    selectedTableResourcesData,
    shouldConnectCodeRepo,
  };
};

export default useWizardTestCatalog;
